import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, useTheme, Grid, Chip, LinearProgress, Card, CardContent, alpha, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Header } from "../../../../components/global/Header";
import FormListToolbar from "../../../../components/global/Toolbar";
import useSWR from 'swr';
import api from '../../../../services/api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { EmptyState } from '../../../../components/global';

const InvoiceTracking = () => {
  const theme = useTheme();
  const [selectedProduction, setSelectedProduction] = useState(null);

  const { data: productions } = useSWR('/productions');
  
  const { data, error, isLoading } = useSWR(
    selectedProduction?.id
      ? `/reports/invoice-tracking?productionId=${selectedProduction.id}`
      : '/reports/invoice-tracking',
    async (url) => {
      const response = await api.get(url);
      return response;
    }
  );

  useEffect(() => {
    if (productions && productions.length > 0) {
      const savedProduction = localStorage.getItem('selectedProduction');
      if (savedProduction) {
        try {
          const parsedProduction = JSON.parse(savedProduction);
          const production = productions.find(p => p.id === parsedProduction.id);
          if (production) {
            setSelectedProduction(production);
          }
        } catch (e) {
          // Invalid saved production, ignore
        }
      }
    }
  }, [productions]);

  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem('selectedProduction', JSON.stringify(selectedProduction));
    }
  }, [selectedProduction]);

  const handleProductionChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedProduction(null);
    } else {
      const production = productions?.find((p) => p.id === value);
      setSelectedProduction(production || null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Παρακολούθηση Τιμολογίων" module="Αναφορές" />
        <EmptyState title="Δεν ήταν δυνατή η φόρτωση των δεδομένων" />
      </Box>
    );
  }

  const report = data || {};
  const summary = report.summary || {};
  const expenses = report.expenses || [];

  // Calculate total amounts for each category
  const totalAmount = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const withInvoiceAmount = expenses
    .filter(e => e.invoiceNo)
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const withoutInvoiceAmount = expenses
    .filter(e => !e.invoiceNo)
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  const columns = [
    {
      field: 'invoiceNo',
      headerName: 'Αρ. Τιμολογίου',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'production',
      headerName: 'Παραγωγή',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'beneficiary',
      headerName: 'Συναλλασσόμενος',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'amount',
      headerName: 'Ποσό',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'date',
      headerName: 'Ημερομηνία',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString('el-GR') : '-',
    },
    {
      field: 'hasFile',
      headerName: 'Έχει Αρχείο',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      renderCell: (params) => (
        params.value ? (
          <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main }} />
        ) : (
          <ErrorOutlineIcon sx={{ color: theme.palette.error.main }} />
        )
      ),
    },
    {
      field: 'fileName',
      headerName: 'Όνομα Αρχείου',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'fileSize',
      headerName: 'Μέγεθος',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatFileSize(params.value),
    },
    {
      field: 'status',
      headerName: 'Κατάσταση',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const status = params.value;
        const config = {
          complete: { color: 'success', label: 'Ολοκληρωμένο', icon: <CheckCircleOutlineIcon /> },
          'no-file': { color: 'warning', label: 'Χωρίς Αρχείο', icon: <WarningIcon /> },
          'no-invoice': { color: 'info', label: 'Χωρίς Τιμολόγιο', icon: <WarningIcon /> },
          incomplete: { color: 'error', label: 'Ατελές', icon: <ErrorOutlineIcon /> },
        };
        const cfg = config[status] || config.incomplete;
        return (
          <Chip
            icon={cfg.icon}
            label={cfg.label}
            color={cfg.color}
            size="small"
          />
        );
      },
    },
    {
      field: 'createdDate',
      headerName: 'Ημ/νία Δημιουργίας',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString('el-GR') : '-',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Παρακολούθηση Τιμολογίων & Εγγράφων" module="Αναφορές" subtitle="Αναφορά τιμολογίων και συνημμένων αρχείων" />

      {/* Production Filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id="prod-label">Παραγωγή</InputLabel>
          <Select
            labelId="prod-label"
            id="prod-id"
            label="Παραγωγή"
            value={selectedProduction?.id || ''}
            onChange={handleProductionChange}
          >
            <MenuItem value="">
              <em>Όλες οι Παραγωγές</em>
            </MenuItem>
            {productions?.map((production) => (
              <MenuItem key={production.id} value={production.id}>
                {production.p_descr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease-in-out',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[6],
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <ReceiptOutlinedIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: 'right',
                  }}
                >
                  {formatCurrency(totalAmount)}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                {summary.totalExpenses || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Σύνολο Εξόδων
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease-in-out',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[6],
                borderColor: theme.palette.success.main,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.success.main,
                    textAlign: 'right',
                  }}
                >
                  {formatCurrency(withInvoiceAmount)}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.success.main,
                  mb: 0.5,
                }}
              >
                {summary.withInvoice || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Με Τιμολόγιο
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease-in-out',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[6],
                borderColor: theme.palette.warning.main,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                  }}
                >
                  <WarningIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.warning.main,
                    textAlign: 'right',
                  }}
                >
                  {formatCurrency(withoutInvoiceAmount)}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.warning.main,
                  mb: 0.5,
                }}
              >
                {summary.withoutInvoice || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Χωρίς Τιμολόγιο
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease-in-out',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[6],
                borderColor: theme.palette.info.main,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                  }}
                >
                  <FolderOutlinedIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: 'right',
                  }}
                >
                  {formatFileSize(summary.totalFileSize || 0)}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                {summary.withFile || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Συνολικό Μέγεθος Αρχείων
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3], height: 'calc(100vh - 470px)', display: 'flex', flexDirection: 'column' }}>
        <FormListToolbar disableAdd />
        <Box sx={{ flex: 1, width: '100%', minHeight: 0 }}>
          <DataGrid
            rows={expenses.map((e, index) => ({ ...e, id: index }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              height: '100%',
              width: '100%',
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default InvoiceTracking;

